import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, History, Loader2, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { R, fmtQty, pct, rangeToDates, RangePicker, StatTile, ExplainerBox, EmptyState, ErrorState, errMsg, fmtWhen, humanize, CONSUMPTION_TONE, CONSUMPTION_LABEL } from "./shared";

/**
 * Session 38 — Inventory → Variance (Theoretical vs Actual).
 *
 *   actual      = opening + received + transfers in − transfers out − closing
 *   theoretical = Σ recipe deductions from sales (auto_deduct ledger rows)
 *   variance    = actual − theoretical
 *              → decomposed into recorded waste, stock-take adjustments,
 *                manual edits, and what is still UNEXPLAINED.
 *
 * All maths is server-side (/api/owner/inventory/variance). This panel only
 * presents it and lets a manager drill into the movement ledger.
 */
export const VariancePanel = ({ http, authHeaders }) => {
  const [range, setRange] = useState("7d");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null); // { row, movements, summary, loading }

  const load = useCallback(async (rangeKey, cat) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...rangeToDates(rangeKey || range) };
      const c = cat || category;
      if (c && c !== "all") params.category = c;
      const r = await http.get("/owner/inventory/variance", { headers: authHeaders, params });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load variance."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, range, category]);

  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range, category); }, []);

  const rows = useMemo(() => {
    const list = data?.rows || [];
    const q = query.trim().toLowerCase();
    return q ? list.filter((r) => String(r.name || "").toLowerCase().includes(q)) : list;
  }, [data, query]);

  const totals = data?.totals || {};
  const unexplainedRows = rows.filter((r) => Math.abs(Number(r.unexplained_qty) || 0) > 1e-6);

  const openDrill = async (row) => {
    setDrill({ row, movements: [], summary: {}, loading: true });
    try {
      const r = await http.get(`/owner/inventory/movements/${row.id}`, { headers: authHeaders, params: { ...rangeToDates(range), limit: 300 } });
      setDrill({ row, movements: r.data.movements || [], summary: r.data.summary_by_reason || {}, loading: false });
    } catch (e) {
      toast.error(errMsg(e, "Unable to load movements."));
      setDrill(null);
    }
  };

  const varianceTone = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) < 1e-6) return "";
    return n > 0 ? "text-destructive font-semibold" : "text-emerald-700 dark:text-emerald-400 font-semibold";
  };

  return (
    <div className="space-y-3" data-testid="variance-panel">
      <ExplainerBox title="What is this tab?" testId="variance-explainer">
        <p>For every ingredient that moved in the period we compare what your recipes say you <strong>should</strong> have used against what the stock ledger says you <strong>actually</strong> used.</p>
        <ul className="list-disc pl-5">
          <li><strong>Theoretical</strong> — recipe quantity × items sold (auto-deducted on every order).</li>
          <li><strong>Actual</strong> — opening + received + transfers in − transfers out − closing.</li>
          <li><strong>Variance</strong> — actual − theoretical. Positive means more stock left the shelf than sales explain.</li>
          <li><strong>Unexplained</strong> — variance minus recorded waste. Stock-take corrections and manual edits land here because nobody attributed a reason to them; this is what to investigate.</li>
        </ul>
      </ExplainerBox>

      <div className="flex flex-wrap items-center gap-2">
        <RangePicker value={range} onChange={(v) => { setRange(v); load(v, category); }} testIdPrefix="variance" />
        <Tabs value={category} onValueChange={(v) => { setCategory(v); load(range, v); }}>
          <TabsList className="h-9 rounded-xl">
            <TabsTrigger value="all" className="rounded-lg text-xs" data-testid="variance-cat-all">All</TabsTrigger>
            <TabsTrigger value="kitchen" className="rounded-lg text-xs" data-testid="variance-cat-kitchen">Kitchen</TabsTrigger>
            <TabsTrigger value="bar" className="rounded-lg text-xs" data-testid="variance-cat-bar">Bar</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:ml-auto sm:w-48">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input type="search" placeholder="Find ingredient…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-9 w-full rounded-xl pl-8" data-testid="variance-search" />
        </div>
        <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={() => load(range, category)} disabled={loading} data-testid="variance-refresh">
          {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}Refresh
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={() => load(range, category)} testId="variance-error" /> : null}

      {loading && !data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatTile label="Theoretical usage" value={R(totals.theoretical_value)} hint="What sales should have used" testId="variance-tile-theoretical" />
          <StatTile label="Actual usage" value={R(totals.actual_value)} hint="What the ledger says left the shelf" testId="variance-tile-actual" />
          <StatTile label="Variance" value={R(totals.variance_value)} hint="Actual − theoretical" tone={Number(totals.variance_value) > 0.005 ? "bad" : Number(totals.variance_value) < -0.005 ? "warn" : "good"} testId="variance-tile-variance" />
          <StatTile label="Recorded waste" value={R(totals.waste_value)} hint="Part of the variance you already explained" tone="muted" testId="variance-tile-waste" />
          <StatTile label="Received" value={R(totals.received_value)} hint={totals.uncosted_ingredients ? `${totals.uncosted_ingredients} ingredient(s) have no cost — values exclude them` : "Deliveries in the period"} tone={totals.uncosted_ingredients ? "warn" : "muted"} testId="variance-tile-received" />
        </div>
      ) : null}

      {data && unexplainedRows.length ? (
        <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10" data-testid="variance-unexplained-card">
          <CardContent className="p-4">
            <p className="text-sm font-semibold">Needs investigation · {unexplainedRows.length} ingredient{unexplainedRows.length === 1 ? "" : "s"}</p>
            <p className="mt-1 text-xs text-muted-foreground">Variance not covered by recorded waste, stock takes or manual edits. Common causes: {(data.investigation_categories || []).slice(0, 6).join(", ").toLowerCase()}.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {unexplainedRows.slice(0, 12).map((r) => (
                <button key={`unx-${r.id}`} type="button" onClick={() => openDrill(r)} className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`variance-unexplained-chip-${r.id}`}>
                  <span className="font-medium">{r.name}</span> <span className={varianceTone(r.unexplained_qty)}>{Number(r.unexplained_qty) > 0 ? "+" : ""}{fmtQty(r.unexplained_qty)} {r.unit}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="rounded-2xl border-border bg-card" data-testid="variance-table-card">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold">Theoretical vs Actual · per ingredient</p>
            {data?.range ? <span className="text-xs text-muted-foreground">{String(data.range.start).slice(0, 10)} → {String(data.range.end).slice(0, 10)}</span> : null}
          </div>
          {loading && !data ? (
            <Skeleton className="h-40 w-full rounded-2xl" />
          ) : rows.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ingredient</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Opening</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Received</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Closing</TableHead>
                    <TableHead className="text-right">Theoretical</TableHead>
                    <TableHead className="hidden text-right md:table-cell" title="Part of theoretical usage supplied by a non-preferred location (fallback) or by no location at all (shortfall)">Fallback / Shortfall</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Waste</TableHead>
                    <TableHead className="text-right">Unexplained</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Value</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const vq = Number(r.variance_qty) || 0;
                    return (
                      <TableRow key={`var-${r.id}`} data-testid={`variance-row-${r.id}`}>
                        <TableCell className="font-medium">
                          <span className="block truncate">{r.name}</span>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.category} · {r.cost_per_unit !== null && r.cost_per_unit !== undefined ? `${R(r.cost_per_unit)}/${r.unit}` : "no cost"}{r.state === "negative" ? " · NEGATIVE STOCK" : r.state === "out" ? " · OUT" : r.state === "low" ? " · LOW" : ""}</span>
                        </TableCell>
                        <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">{fmtQty(r.opening_stock)}</TableCell>
                        <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">{Number(r.received) > 0 ? `+${fmtQty(r.received)}` : "—"}</TableCell>
                        <TableCell className="hidden text-right text-xs tabular-nums lg:table-cell">{fmtQty(r.closing_stock)}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{fmtQty(r.theoretical_usage)} <span className="text-muted-foreground">{r.unit}</span></TableCell>
                        <TableCell className="hidden text-right text-xs tabular-nums md:table-cell" data-testid={`variance-consumption-${r.id}`}>
                          {Number(r.consumption?.fallback) > 1e-6 || Number(r.consumption?.shortfall) > 1e-6 ? (
                            <span className="inline-flex flex-wrap justify-end gap-1">
                              {Number(r.consumption?.fallback) > 1e-6 ? <Badge variant="outline" className={`rounded-full text-[9px] ${CONSUMPTION_TONE.fallback}`}>FB {fmtQty(r.consumption.fallback)}</Badge> : null}
                              {Number(r.consumption?.shortfall) > 1e-6 ? <Badge variant="outline" className={`rounded-full text-[9px] ${CONSUMPTION_TONE.shortfall}`}>SF {fmtQty(r.consumption.shortfall)}</Badge> : null}
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{fmtQty(r.actual_usage)} <span className="text-muted-foreground">{r.unit}</span></TableCell>
                        <TableCell className={`text-right text-xs tabular-nums ${varianceTone(vq)}`}>
                          <span className="inline-flex items-center gap-0.5">{vq > 1e-6 ? <ArrowUpRight className="h-3 w-3" /> : vq < -1e-6 ? <ArrowDownRight className="h-3 w-3" /> : null}{vq > 0 ? "+" : ""}{fmtQty(vq)}</span>
                          {r.variance_pct !== null && r.variance_pct !== undefined ? <span className="block text-[10px] font-normal text-muted-foreground">{pct(r.variance_pct)}</span> : null}
                        </TableCell>
                        <TableCell className="hidden text-right text-xs tabular-nums md:table-cell">{Number(r.explained?.waste) > 0 ? fmtQty(r.explained.waste) : "—"}</TableCell>
                        <TableCell className={`text-right text-xs tabular-nums ${varianceTone(r.unexplained_qty)}`}>{Math.abs(Number(r.unexplained_qty) || 0) > 1e-6 ? `${Number(r.unexplained_qty) > 0 ? "+" : ""}${fmtQty(r.unexplained_qty)}` : <span className="text-muted-foreground">0</span>}</TableCell>
                        <TableCell className={`hidden text-right text-xs tabular-nums sm:table-cell ${varianceTone(r.variance_value)}`}>{R(r.variance_value)}</TableCell>
                        <TableCell className="text-right">
                          <Button type="button" size="sm" variant="ghost" className="h-8 rounded-lg px-2" onClick={() => openDrill(r)} title="Why did this move?" data-testid={`variance-drill-${r.id}`}><History className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : data ? (
            <EmptyState title="No stock movements in this period" body="Ingredients appear here as soon as an order deducts them, stock is received, waste is logged or a stock take is posted." testId="variance-empty" />
          ) : null}
          {data?.method ? <p className="mt-3 text-[11px] text-muted-foreground" data-testid="variance-method">Method: {data.method}</p> : null}
        </CardContent>
      </Card>

      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) setDrill(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-3xl" data-testid="variance-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drill?.row?.name} · movements</DialogTitle>
            <DialogDescription>Every ledger entry for this ingredient in the selected period. Reason totals first, then the individual rows.</DialogDescription>
          </DialogHeader>
          {drill?.loading ? (
            <Skeleton className="h-40 w-full rounded-2xl" />
          ) : drill ? (
            <div className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-3">
                <StatTile label="Theoretical" value={`${fmtQty(drill.row.theoretical_usage)} ${drill.row.unit}`} testId="drill-theoretical" tone="muted" />
                <StatTile label="Actual" value={`${fmtQty(drill.row.actual_usage)} ${drill.row.unit}`} testId="drill-actual" tone="muted" />
                <StatTile label="Unexplained" value={`${Number(drill.row.unexplained_qty) > 0 ? "+" : ""}${fmtQty(drill.row.unexplained_qty)} ${drill.row.unit}`} tone={Math.abs(Number(drill.row.unexplained_qty) || 0) > 1e-6 ? "warn" : "good"} testId="drill-unexplained" />
              </div>
              {drill.row.consumption ? (
                <p className="text-xs text-muted-foreground" data-testid="drill-consumption">
                  Theoretical {fmtQty(drill.row.theoretical_usage)} {drill.row.unit} = normal {fmtQty(drill.row.consumption.normal)}
                  {Number(drill.row.consumption.fallback) > 1e-6 ? ` + fallback ${fmtQty(drill.row.consumption.fallback)} (drawn from a non-preferred location — ordinary usage, not variance)` : ""}
                  {Number(drill.row.consumption.shortfall) > 1e-6 ? ` + shortfall ${fmtQty(drill.row.consumption.shortfall)} (no location had stock — the balance went negative; NOT genuine usage until a count confirms it)` : ""}
                  {Number(drill.row.consumption.restored) > 1e-6 ? ` − restored ${fmtQty(drill.row.consumption.restored)} (voids)` : ""}
                  {drill.row.consumption.by_location?.length ? ` · by location: ${drill.row.consumption.by_location.map((b) => `${b.location_name} ${fmtQty(b.quantity)}`).join(", ")}` : ""}
                </p>
              ) : null}
              {drill.row.explained?.waste_by_reason && Object.keys(drill.row.explained.waste_by_reason).length ? (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(drill.row.explained.waste_by_reason).map(([k, v]) => <Badge key={k} variant="outline" className="rounded-full">{humanize(k)}: {fmtQty(v)} {drill.row.unit}</Badge>)}
                </div>
              ) : null}
              {drill.row.explained ? (
                <p className="text-xs text-muted-foreground" data-testid="drill-decomposition">
                  Variance {Number(drill.row.variance_qty) > 0 ? "+" : ""}{fmtQty(drill.row.variance_qty)} {drill.row.unit} = recorded waste {fmtQty(drill.row.explained.waste)}
                  {Math.abs(Number(drill.row.explained.stock_take_adjustments) || 0) > 1e-6 ? ` · stock-take corrections ${Number(drill.row.explained.stock_take_adjustments) > 0 ? "+" : ""}${fmtQty(drill.row.explained.stock_take_adjustments)}` : ""}
                  {Math.abs(Number(drill.row.explained.manual_adjustments) || 0) > 1e-6 ? ` · manual edits ${Number(drill.row.explained.manual_adjustments) > 0 ? "+" : ""}${fmtQty(drill.row.explained.manual_adjustments)}` : ""}
                  {Math.abs(Number(drill.row.explained.other) || 0) > 1e-6 ? ` · other ${Number(drill.row.explained.other) > 0 ? "+" : ""}${fmtQty(drill.row.explained.other)}` : ""}
                  {" "}(a positive correction means the count found MORE than the ledger expected).
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1.5" data-testid="drill-summary">
                {Object.entries(drill.summary).map(([k, v]) => (
                  <Badge key={k} variant="secondary" className="rounded-full">{humanize(k)} · {v.count}× · in {fmtQty(v.in)} / out {fmtQty(v.out)}</Badge>
                ))}
              </div>
              {drill.movements.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                        <TableHead className="hidden text-right sm:table-cell">Balance</TableHead>
                        <TableHead className="hidden md:table-cell">By / note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drill.movements.map((m) => {
                        const d = Number(m.difference) || 0;
                        return (
                          <TableRow key={m.id} data-testid={`drill-row-${m.id}`}>
                            <TableCell className="text-xs tabular-nums">{fmtWhen(m.created_at)}</TableCell>
                            <TableCell className="text-xs">
                              {humanize(m.reason)}{m.waste_reason ? ` · ${humanize(m.waste_reason)}` : ""}{m.supplier ? ` · ${m.supplier}` : ""}{m.invoice_number ? ` #${m.invoice_number}` : ""}{m.order_number ? ` · ${m.order_number}` : ""}
                              {m.consumption_type && m.consumption_type !== "normal" ? <Badge variant="outline" className={`ml-1 rounded-full text-[9px] ${CONSUMPTION_TONE[m.consumption_type] || ""}`}>{CONSUMPTION_LABEL[m.consumption_type] || humanize(m.consumption_type)}</Badge> : null}
                              {m.reason === "transfer" ? <span className="ml-1 text-[10px] text-muted-foreground">{m.from_location_name} → {m.to_location_name}</span> : m.location_name ? <span className="ml-1 text-[10px] uppercase text-muted-foreground">{m.location_name}</span> : m.item_type === "ingredient_boh" ? <span className="ml-1 text-[10px] uppercase text-muted-foreground">back of house</span> : null}
                            </TableCell>
                            <TableCell className={`text-right text-xs tabular-nums ${d < 0 ? "text-destructive" : "text-emerald-700 dark:text-emerald-400"}`}>{d > 0 ? "+" : ""}{fmtQty(d)} {m.unit}</TableCell>
                            <TableCell className="hidden text-right text-xs tabular-nums sm:table-cell">{fmtQty(m.new_value)}</TableCell>
                            <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{m.user || "—"}{m.notes ? ` · ${m.notes}` : ""}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <EmptyState title="No movements in this period" testId="drill-empty" />
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
